import { v4 as uuidv4 } from 'uuid'
import { RpcError } from 'grpc-web'
import { ERROR_MESSAGES } from 'src/Utils/globalEnums'
import {
  parseDuplicateRequirementNameError,
  parseDuplicateSectionNameError,
} from './AddFramework/FrameworkSectionsCSV.helpers'
import {
  FrameworkSectionsEditorErrors,
  RequirementInfo,
  SectionInfo,
} from './FrameworkSectionsEditor.types'

export const createEmptyRequirementDraft = (): RequirementInfo => ({
  tempId: uuidv4(),
  name: '',
  description: '',
  controlIds: [],
})

export const createEmptySectionDraft = (): SectionInfo => ({
  tempId: uuidv4(),
  name: '',
  description: '',
  requirements: [],
})

export const getEmptyNameErrors = (
  sections: SectionInfo[],
): {
  sectionNames: Record<string, string>
  requirementNames: Record<string, string>
} => {
  const sectionNames: Record<string, string> = {}
  const requirementNames: Record<string, string> = {}
  for (const section of sections) {
    if (!section.name.trim()) {
      sectionNames[section.tempId] = ERROR_MESSAGES.EMPTY
    }
    for (const requirement of section.requirements) {
      if (!requirement.name.trim()) {
        requirementNames[requirement.tempId] = ERROR_MESSAGES.EMPTY
      }
    }
  }
  return { sectionNames, requirementNames }
}

const normalize = (name: string): string => name.trim().toLowerCase()

const SECTION_DUP_EXISTING = 'Section name already exists in this framework.'
const SECTION_DUP_WITHIN = 'Duplicate section name.'
const REQUIREMENT_DUP_EXISTING =
  'Requirement name already exists in this framework.'
const REQUIREMENT_DUP_WITHIN = 'Duplicate requirement name.'

/**
 * Detects duplicate section/requirement names within the submit and against
 * existing names. Each offending tempId gets an error string.
 */
export const getDuplicateNameErrors = (
  sections: SectionInfo[],
  existingSectionNames: string[] = [],
  existingRequirementNames: string[] = [],
): {
  sectionNames: Record<string, string>
  requirementNames: Record<string, string>
} => {
  const sectionNames: Record<string, string> = {}
  const requirementNames: Record<string, string> = {}
  const existingSectionSet = new Set(
    existingSectionNames.map(normalize).filter(Boolean),
  )
  const existingRequirementSet = new Set(
    existingRequirementNames.map(normalize).filter(Boolean),
  )

  const sectionTempIdsByKey = new Map<string, string[]>()
  const requirementTempIdsByKey = new Map<string, string[]>()
  for (const section of sections) {
    const key = normalize(section.name)
    if (key) {
      const list = sectionTempIdsByKey.get(key) ?? []
      list.push(section.tempId)
      sectionTempIdsByKey.set(key, list)
    }
    for (const requirement of section.requirements) {
      const rKey = normalize(requirement.name)
      if (!rKey) continue
      const list = requirementTempIdsByKey.get(rKey) ?? []
      list.push(requirement.tempId)
      requirementTempIdsByKey.set(rKey, list)
    }
  }

  for (const [key, tempIds] of sectionTempIdsByKey) {
    if (existingSectionSet.has(key)) {
      tempIds.forEach((id) => {
        sectionNames[id] = SECTION_DUP_EXISTING
      })
    } else if (tempIds.length > 1) {
      tempIds.forEach((id) => {
        sectionNames[id] = SECTION_DUP_WITHIN
      })
    }
  }
  for (const [key, tempIds] of requirementTempIdsByKey) {
    if (existingRequirementSet.has(key)) {
      tempIds.forEach((id) => {
        requirementNames[id] = REQUIREMENT_DUP_EXISTING
      })
    } else if (tempIds.length > 1) {
      tempIds.forEach((id) => {
        requirementNames[id] = REQUIREMENT_DUP_WITHIN
      })
    }
  }
  return { sectionNames, requirementNames }
}

export const findRequirementTempIdByName = (
  sections: SectionInfo[],
  name: string,
): string | undefined => {
  const target = normalize(name)
  if (!target) return undefined
  for (const section of sections) {
    for (const requirement of section.requirements) {
      if (normalize(requirement.name) === target) return requirement.tempId
    }
  }
  return undefined
}

/**
 * Builds in-panel CSV error messages for duplicate sections / requirements.
 * Each unique offending name yields one message.
 */
export const getCsvDuplicateMessages = (
  sections: SectionInfo[],
  existingSectionNames: string[] = [],
  existingRequirementNames: string[] = [],
): string[] => {
  const existingSectionSet = new Set(
    existingSectionNames.map(normalize).filter(Boolean),
  )
  const existingRequirementSet = new Set(
    existingRequirementNames.map(normalize).filter(Boolean),
  )
  const sectionsByKey = new Map<string, SectionInfo[]>()
  const requirementsByKey = new Map<string, RequirementInfo[]>()
  for (const section of sections) {
    const key = normalize(section.name)
    if (key) {
      const list = sectionsByKey.get(key) ?? []
      list.push(section)
      sectionsByKey.set(key, list)
    }
    for (const r of section.requirements) {
      const rKey = normalize(r.name)
      if (!rKey) continue
      const list = requirementsByKey.get(rKey) ?? []
      list.push(r)
      requirementsByKey.set(rKey, list)
    }
  }

  const messages: string[] = []
  for (const [key, list] of sectionsByKey) {
    const display = list[0].name.trim()
    if (existingSectionSet.has(key)) {
      messages.push(`Section "${display}" already exists in this framework.`)
    } else if (list.length > 1) {
      messages.push(`Duplicate section name "${display}".`)
    }
  }
  for (const [key, list] of requirementsByKey) {
    const display = list[0].name.trim()
    if (existingRequirementSet.has(key)) {
      messages.push(
        `Requirement "${display}" already exists in this framework.`,
      )
    } else if (list.length > 1) {
      messages.push(`Duplicate requirement name "${display}".`)
    }
  }
  return messages
}

/**
 * Maps a backend submit error to inline editor errors or a toast message
 * when the error represents a duplicate section or requirement name. Returns
 * null if not a recognized duplicate.
 */
export const parseBackendDuplicateError = (
  err: unknown,
  sections: SectionInfo[],
): {
  editorErrors?: FrameworkSectionsEditorErrors
  toast?: string
} | null => {
  const rpcErr = err as RpcError
  const sectionDupName = parseDuplicateSectionNameError(rpcErr?.message ?? '')
  if (sectionDupName) {
    const sectionNames: Record<string, string> = {}
    sections.forEach((section) => {
      if (normalize(section.name) === normalize(sectionDupName)) {
        sectionNames[section.tempId] = SECTION_DUP_EXISTING
      }
    })
    return { editorErrors: { sectionNames } }
  }

  const dupInfo = parseDuplicateRequirementNameError(rpcErr?.message ?? '')
  if (!dupInfo) return null

  const message = dupInfo.isExisting
    ? `Requirement "${dupInfo.name}" already exists in this framework.`
    : `Duplicate requirement name "${dupInfo.name}".`
  const tempId = findRequirementTempIdByName(sections, dupInfo.name)
  if (tempId) {
    return { editorErrors: { requirementNames: { [tempId]: message } } }
  }
  return { toast: message }
}
